import boxx from '@baidu/boxx-fe';
// @ts-ignore
import feedDescription from '@baidu/boxx-fe/dist/feedDescription.json';
import {injectLuiCosmicToken} from '@baidu/lui/token';
import {COIN_TOTAL_API, YIYAN_ASK_API} from '@lib/constants/api';
import {sendYaLog} from '@lib/log';
import {systemInfo} from '@lib/utils/browser';
import {registerDataChannel, unRegisterDataChannel} from '@lib/utils/datachannel';
import {getGlobalSettings, SystemThemeData} from '@lib/utils/native';
import {appVersionCompare, inNewApp} from '@lib/utils/tools';
import {isInNewApp} from './env';
import {dispatchMessage, listenMessage} from './messageCenter';
import {
    DEVICE_INFO,
    FONT_LEVEL,
    getDefaultSkin,
    setPageFont,
    setPageMode,
    SKIN_MODE,
    THEME_MODE,
    TOKEN_TYPE,
} from './pageMode';

interface DefaultThemeRes {
    [key: string]: string;
}
// 前端主题切换事件emitter通知
export const H5ChangeThemeAction = 'h5ChangeThemeAction';
export interface ThemeResponse {
    theme?: THEME_MODE;
    skin?: SKIN_MODE;
    ext?: {
        // 强制设置Skin值为当前设置的skin值，不由端上暗黑切换导致的Skin变化而变化
        // （端上Skin：vip和模型相关，当业务方Skin:vip不和模型相关时需要强制制定）
        forceSkin?: boolean;
        forceTheme?: boolean;
        // 调起来源，用于区分是哪个页面发起的通知
        from?: string;
    };
}

/**
 * 用户自定义配置
 */
interface UserDefinedOptions {
    /**
     * 是否开启
     */
    open: boolean;
    /**
     * 自定义主题
     */
    theme: THEME_MODE;
    /**
     * 自定义皮肤
     */
    skin: SKIN_MODE;
}

export interface ThemeAdapterOptions {
    useSkin?: boolean;
}
export interface PageAdapterOptions {
    // 开启主题模式适配
    openThemeAdapter?: ThemeAdapterOptions | boolean;
    // 开启字体Css变量，字体大小可使用变量，默认从端上读取字号
    openFontAdapter?:
        | {
              // font字体大小适配,强制设置字号
              fontLevel?: FONT_LEVEL;
          }
        | boolean;
    // 关闭Rem动态适配，窗口大小变化，1rem的值跟着变化， 【默认开启】
    closeRemAdapter?: boolean;
}

const ThemeClassify: {
    [key in SKIN_MODE | THEME_MODE | DEVICE_INFO]: string;
} = {
    basic: 'cos-newapp-basic',
    basicVip: 'cos-newapp-basic-vip',
    youth: 'cos-newapp-youth',
    youthVip: 'cos-newapp-youth-vip',
    pcClient: 'c-pc-client',
    vip: 'c-vip',
    light: 'c-light',
    dark: 'c-dark',
    ios: 'c-ios',
    android: 'c-android',
};

// ////////////////////******调试使用*******/////////////////////
// 仅线下生效，在初始化阶段强制使用何种模式
export const __DevThemeOfflineOpt__ = {
    // 是否开启强制模式，开启则应用后面的配置
    open: false,
    theme: THEME_MODE.Dark,
    skin: SKIN_MODE.Vip,
};
// ////////////////////******调试使用*******/////////////////////
// eslint-disable-next-line xss/no-mixed-html
const $html = document.querySelector('html');
const FontStyleIdInHead = 'self-define-font';
const ThemeStyleIdInHead = 'self-define-style';
const NA_SEND_THEME_CHANNEL = 'com.baidu.newapp.channel.theme_change';
// const H5_SEND_THEME_CHANNEL = 'com.baidu.newapp.h5.theme_change';
const ThemeCenterPath = 'themeCenter';
let curTheme = THEME_MODE.Light;
let curSkin: SKIN_MODE = getDefaultSkin();
// 使用了registerPageAdapter的都是新的TOKEN编码
const curTokenType = TOKEN_TYPE.Theme;
const ForceOptions = {
    forceSkin: false,
    forceTheme: false,
};
// debug阶段默认输出的日志，方便各角色查看日志信息
const __prefix = '__暗黑系统日志__: ';

const debugConsole = () => {
    if (!__IS_DEBUG__) {
        return;
    }

    const askApi = new URL(YIYAN_ASK_API);
    const coinApi = new URL(COIN_TOTAL_API);
    console.info(`${__prefix}当前真实使用的server环境是：`, `${askApi.protocol}//${askApi.host}`);
    console.info(`${__prefix}当前真实使用的FE环境是：`, window.location.href);
    console.info(`${__prefix}灵感币服务环境是(**涉及到灵感币的页面关注**)：`, `${coinApi.protocol}//${coinApi.host}`);
};

const curPage = window.location.href;
const listenChatTheme = (callback: (data: DefaultThemeRes) => void) => {
    // 单点通信监听
    // const processChatTheme = (_action: string, data: string) => {
    //     const res: DefaultThemeRes = JSON.parse(data);
    // callback(res);
    // };
    // listenMessage(NA_SEND_THEME_CHANNEL, processChatTheme);
    // 数据通道监听
    const themeUpdateCallback = (_action: string, data: string) => {
        const res: DefaultThemeRes = JSON.parse(data);
        callback(res || '');
    };
    registerDataChannel(NA_SEND_THEME_CHANNEL, themeUpdateCallback);
};

/**
 * // 暂时不需要H5向NA发送消息
 * 发送主题类消息到端上
 */
// const sendChatTheme = () => {
//     sendMessage(H5_SEND_THEME_CHANNEL, {});
// }

/**
 * 广播主题、皮肤变化信息同步到其他页面， 其他页面监听
 */
const sendThemeResponseToH5 = () => {
    dispatchMessage(
        H5ChangeThemeAction,
        JSON.stringify({
            theme: curTheme,
            skin: curSkin,
            ext: {
                from: ThemeCenterPath,
            },
        })
    );
};

// 挂载到根dom上,为了确保纯html模板head中style仍生效
const addHtmlClsWithName = (clsName?: string) => {
    if (clsName && !$html?.classList.contains(clsName)) {
        $html?.classList.add(clsName);
    }
};
// 移除根dom上的class
const removeHtmlClsWithName = (clsName: string) => {
    if ($html && clsName && $html.classList.contains(clsName)) {
        $html.classList.remove(clsName);
    }
};

const _setThemeAndSkin = (theme: THEME_MODE, skin?: SKIN_MODE) => {
    if (theme && theme !== curTheme) {
        removeHtmlClsWithName(ThemeClassify[curTheme]);
    }

    curTheme = theme;
    addHtmlClsWithName(ThemeClassify[curTheme]);

    removeHtmlClsWithName((ThemeClassify as any)[curSkin]);
    const defaultSkin = getDefaultSkin();
    curSkin = skin || defaultSkin;
    addHtmlClsWithName((ThemeClassify as any)[curSkin]);

    // 直接把线下的逻辑抽出来，方便调试，不耦合在上面的逻辑了
    if (__IS_DEBUG__ && __DevThemeOfflineOpt__.open) {
        removeHtmlClsWithName(ThemeClassify[curTheme]);
        curTheme = __DevThemeOfflineOpt__.theme;
        addHtmlClsWithName(ThemeClassify[curTheme]);
        removeHtmlClsWithName((ThemeClassify as any)[curSkin]);
        curSkin = __DevThemeOfflineOpt__.skin;
        addHtmlClsWithName((ThemeClassify as any)[curSkin]);
    }
};

const insertStyleBeforeScript = (styleDom: HTMLElement) => {
    const headElement = document.head;
    const scriptElements = headElement.querySelectorAll('script');

    const firstScriptElement = scriptElements.length > 0 ? scriptElements[0] : null;
    if (firstScriptElement) {
        headElement.insertBefore(styleDom, firstScriptElement);
    } else {
        headElement.appendChild(styleDom);
    }
};

export const addThemeClassContainer = (elementId: string) => {
    const themeStyle = document.getElementById(elementId);
    if (themeStyle) {
        const parentElement = themeStyle.parentElement as HTMLElement;
        parentElement?.removeChild(themeStyle);
    }
    const styleElement = document.createElement('style');
    styleElement.id = elementId;
    insertStyleBeforeScript(styleElement);
};

export const initGlobalThemeEventOn = () => {
    // 监听主对话切换模型事件
    // TODO 目前模型切换是作为一种vip皮肤，后续端上高版本做vip皮肤切换时，低版本仍需保留
    listenMessage(
        H5ChangeThemeAction,
        (action, res: string) => {
            const themeRes = JSON.parse(res);
            const skin = isInNewApp ? themeRes.skin : curSkin;
            const theme = themeRes.theme || curTheme;
            const messageFromSelf = themeRes.ext?.from === ThemeCenterPath;
            // 避免自身默认设置覆盖掉外界主动设置皮肤
            if (messageFromSelf) {
                return;
            }
            if (typeof themeRes?.ext?.forceSkin === 'boolean') {
                ForceOptions.forceSkin = !!themeRes.ext?.forceSkin;
            }
            if (typeof themeRes?.ext?.forceTheme === 'boolean') {
                ForceOptions.forceTheme = !!themeRes.ext?.forceTheme;
            }
            _setThemeAndSkin(theme, skin);
            sendYaLog(`theme_mode_GlobalThemeEvent_${JSON.stringify(themeRes)}
            _cur_setting_theme_${curTheme}_skin_${curSkin}
            _cur_force_opt_${JSON.stringify(ForceOptions)}_in_page_${curPage}`);
            setPageMode(curTheme, curSkin, curTokenType);
        },
        true
    );
};

/**
 * theme监听
 * @param themeOptions 主题配置
 * @param userDefinedOptions 用户自定义皮肤信息， 优先级最高
 * @returns void
 */
const appendThemeListener = async (
    themeOptions: ThemeAdapterOptions | boolean,
    userDefinedOptions?: UserDefinedOptions
) => {
    if (userDefinedOptions && userDefinedOptions.open) {
        setPageMode(userDefinedOptions.theme, userDefinedOptions.skin, curTokenType);
        _setThemeAndSkin(userDefinedOptions.theme, userDefinedOptions.skin);
        return;
    }
    const settings = ['theme'];
    // 只有皮肤模式开启时，才需要获取皮肤信息， 1.11只有主对话&二级对话页需要获取皮肤信息
    if (typeof themeOptions === 'object' && themeOptions.useSkin) {
        settings.push('skin');
    } else {
        // 不使用skin时，业务则不关心皮肤信息得切换
        ForceOptions.forceSkin = true;
    }
    // 获取端上状态
    const naThemeRes = await getGlobalSettings(settings);
    if (naThemeRes && naThemeRes.data) {
        const {theme, skin} = naThemeRes.data as unknown as SystemThemeData['data'];
        _setThemeAndSkin(theme || THEME_MODE.Light, skin);
        __IS_DEBUG__ && console.info(`${__prefix}客户端返回的主题信息theme: `, theme, '  skin: ', skin);
    }
    sendYaLog(`theme_mode_init_na_${naThemeRes ? JSON.stringify(naThemeRes) : 'null'}
        _cur_setting_theme_${curTheme}_skin_${curSkin}_in_page_${curPage}`);
    setPageMode(curTheme, curSkin, curTokenType);

    // 数据通道监听
    window.addEventListener('beforeunload', () => {
        unRegisterDataChannel(NA_SEND_THEME_CHANNEL);
    });

    listenChatTheme((themeData: any) => {
        if (!themeData) {
            sendYaLog(`theme_mode_listen_na_null_in_page_${curPage}}`);
            return;
        }
        let {theme, skin} = themeData as SystemThemeData['data'];
        if (ForceOptions.forceSkin) {
            skin = curSkin;
        }
        if (ForceOptions.forceTheme) {
            theme = curTheme;
        }
        _setThemeAndSkin(theme, skin);
        sendYaLog(`theme_mode_listen_na_${JSON.stringify(themeData)}
            _cur_setting_theme_${curTheme}_skin_${curSkin}
            _cur_force_opt_${JSON.stringify(ForceOptions)}_in_page_${curPage}`);
        setPageMode(curTheme, curSkin, curTokenType);
        sendThemeResponseToH5();
    });

    initGlobalThemeEventOn();
};

const appendRootFont = async (level?: FONT_LEVEL) => {
    addThemeClassContainer(FontStyleIdInHead);
    await setPageFont(level);
};
const appendRootCssVariable = () => {
    addThemeClassContainer(ThemeStyleIdInHead);
};

const appendHostClass = () => {
    if (systemInfo().isAndroid) {
        addHtmlClsWithName(ThemeClassify.android);
    } else if (systemInfo().isIOS) {
        addHtmlClsWithName(ThemeClassify.ios);
    }
};

const appendRemAdapter = (closeAdapter?: boolean) => {
    if (closeAdapter) {
        return;
    }
    (function (doc, win) {
        const docEl = doc.documentElement;
        const resizeEvt = 'orientationchange' in win ? 'orientationchange' : 'resize';
        const recalc = function () {
            const clientWidth = docEl.clientWidth || 414;
            const pixelWidth = clientWidth * window.devicePixelRatio;
            let fontSize = '100px';
            // 高分辩率屏幕适配
            if (clientWidth >= 414 && pixelWidth >= 1440 && window.devicePixelRatio >= 3.5) {
                fontSize = `${100 * 1.06}px`;
            } else if (
                clientWidth >= 414
                && (
                    window.devicePixelRatio === 1
                    // || clientWidth < 378
                    || pixelWidth > 1242
                    || Math.abs(pixelWidth - 1242) / 1242 < 0.05
                )
            ) {
                fontSize = '100px';
            } else {
                fontSize = `${100 * (clientWidth / 414)}px`;
            }
            docEl.style.fontSize = fontSize;
        };
        recalc();
        win.addEventListener(resizeEvt, recalc, false);
    })(document, window);
};

const appendBoxxDescription = () => {
    if (inNewApp) {
        boxx.setDefaultDescription(feedDescription);
        // boxx.setDebug(console.log);
    }
};

const getScreenOrientation = () => {
    if (window.matchMedia('(orientation: portrait)').matches) {
        removeHtmlClsWithName('c-landscape');
        addHtmlClsWithName('c-portrait');
    } else if (window.matchMedia('(orientation: landscape)').matches) {
        removeHtmlClsWithName('c-portrait');
        addHtmlClsWithName('c-landscape');
    }
};

/**
 * 注册适配主题模式
 * @param options ThemeOptions
 * @param userDefinedOptions UserDefinedOptions 用户自定义皮肤配置，优先级最高，不响应皮肤和主题变化
 * {open: boolean, theme: THEME_MODE, skin: SKIN_MODE}
 */
export const registerPageAdapter = async (options?: PageAdapterOptions, userDefinedOptions?: UserDefinedOptions) => {
    debugConsole();
    // 添加宿主类
    appendHostClass();
    // 判断横屏或竖屏方向
    getScreenOrientation();
    window.addEventListener('resize', getScreenOrientation);
    // 添加boxx描述表
    appendBoxxDescription();
    if (options?.openThemeAdapter) {
        // 添加css变量
        appendRootCssVariable();
        // 添加theme监听
        await appendThemeListener(options?.openThemeAdapter, userDefinedOptions);
    }
    if (options?.openFontAdapter) {
        const fontLevel
            = (typeof options.openFontAdapter === 'object' && options.openFontAdapter.fontLevel) || undefined;
        await appendRootFont(fontLevel);
    }
    !options?.closeRemAdapter && appendRemAdapter(options?.closeRemAdapter);
    injectLuiCosmicToken();
};

/**
 * 获取当前主题
 * @returns 当前主题
 */
export const getCurrentTheme = (): THEME_MODE => {
    return curTheme;
};

/**
 * 获取当前皮肤
 * @returns 当前皮肤
 */
export const getCurrentSkin = (): SKIN_MODE => {
    // 兼容低版本，低版本会员会传入vip
    if (appVersionCompare('3.0') >= 0) {
        return curSkin;
    }
    if (curSkin === 'vip') {
        return SKIN_MODE.Vip;
    }
    return curSkin;
};

export const updateThemeAndSkin = (theme: THEME_MODE, skin: SKIN_MODE) => {
    _setThemeAndSkin(theme, skin);
    setPageMode(curTheme, curSkin, curTokenType);
    sendThemeResponseToH5();
};
